import { Tracer } from '@opentelemetry/api';
import { ILogger } from 'app/tracing/logging';
import proj4 from 'proj4';
import { MapViewChangedNotification } from 'app/unity/ContentViewerTypes/MapViewChangedNotification';
import { BaseMapInfo } from 'app/unity/ContentViewerTypes/BaseMapInfo';
import { TileData } from 'app/unity/ContentViewerTypes/TileData';
import { VisibleTiles } from 'app/unity/ContentViewerTypes/VisibleTiles';
import { MapTile } from 'app/unity/ContentViewerTypes/MapTile';

import { WMTSTileSet, WMTSUtils } from './WMTSUtils';
import { WMTSTileSetFetcher } from './WMTSTileSetFetcher';

export class MapTileFetcher {
    private logger: ILogger;
    private tracer: Tracer;
    private wmtsUtils: WMTSUtils;
    private wmtsTileSetFetcher: WMTSTileSetFetcher;
    private wmtsTileSet?: WMTSTileSet;

    public constructor(logger: ILogger, tracer: Tracer, wmtsUtils: WMTSUtils, wmtsTileSetFetcher: WMTSTileSetFetcher) {
        this.logger = logger;
        this.tracer = tracer;
        this.wmtsUtils = wmtsUtils;
        this.wmtsTileSetFetcher = wmtsTileSetFetcher;
    }

    private async ensureTileSetAsync() {
        if (!this.wmtsTileSet) {
            this.wmtsTileSet = await this.wmtsTileSetFetcher.getWMTSTileSetAsync();
        }
    }

    private blobToBase64Async(blob: Blob): Promise<string> {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        return new Promise(resolve => {
            reader.onloadend = () => {
                resolve(reader.result as string);
            };
        });
    }

    private async getTileBlobAsync(
        tileSet: WMTSTileSet,
        tileMatrixID: string,
        tileX: number,
        tileY: number,
    ): Promise<Blob> {
        const url = new URL(tileSet.getTileURL);
        url.searchParams.set('Service', 'WMTS');
        url.searchParams.set('Request', 'GetTile');
        url.searchParams.set('Version', '1.0.0');
        url.searchParams.set('Layer', tileSet.layerID);
        url.searchParams.set('Style', tileSet.styleID);
        url.searchParams.set('Format', tileSet.formatID);
        url.searchParams.set('TileMatrixSet', tileSet.tileMatrixSetID);
        url.searchParams.set('TileMatrix', tileMatrixID);
        url.searchParams.set('TileRow', `${tileY}`);
        url.searchParams.set('TileCol', `${tileX}`);

        const response = await fetch(url);
        return response.blob();
    }

    public async getApplicableBaseMapAsync(request: MapViewChangedNotification) {
        const span = this.tracer.startSpan('getApplicableBaseMap');

        await this.ensureTileSetAsync();

        const tileMatrixSet = this.wmtsTileSet.tileMatrixSet;
        const initialTileMatrix = this.wmtsUtils.getFirst(tileMatrixSet?.TileMatrix);
        if (!(tileMatrixSet && initialTileMatrix)) {
            throw new Error('The tileset was not initialized properly.');
        }

        let lastValidTileMatrixIndex: number | undefined = undefined;
        let lastValidTileMatrix = initialTileMatrix;
        let lastValidTiles: VisibleTiles[] | undefined = undefined;
        const tileMatrixCount = Array.isArray(tileMatrixSet.TileMatrix) ? tileMatrixSet.TileMatrix.length : 1;
        for (let tileMatrixIndex = 0; tileMatrixIndex < tileMatrixCount; ++tileMatrixIndex) {
            const tileMatrix = Array.isArray(tileMatrixSet.TileMatrix)
                ? tileMatrixSet.TileMatrix[tileMatrixIndex]
                : initialTileMatrix;

            const tiles = this.wmtsUtils.boundingBoxToTiles(request.boundingBox, tileMatrixSet, tileMatrix);
            if (!tiles) {
                throw new Error(
                    `Failed to get tiles for bounding box in tile matrix ${tileMatrixIndex}: ${JSON.stringify(request.boundingBox)}`,
                );
            }

            lastValidTileMatrixIndex = tileMatrixIndex;
            lastValidTileMatrix = tileMatrix;
            lastValidTiles = tiles.tiles;
            const boundingBoxPixelsX = tiles.tileCountX * initialTileMatrix.TileWidth;
            const boundingBoxPixelsY = tiles.tileCountY * initialTileMatrix.TileHeight;
            const latitudeRange = request.boundingBox.max.latitude - request.boundingBox.min.latitude;
            const longitudeRange = request.boundingBox.max.longitude - request.boundingBox.min.longitude;
            const minimumPixelDensity = {
                longitude: 1280 * (longitudeRange >= latitudeRange ? 1.0 : longitudeRange / latitudeRange),
                latitude: 1280 * (latitudeRange >= longitudeRange ? 1.0 : latitudeRange / longitudeRange),
            };
            if (
                boundingBoxPixelsX >= minimumPixelDensity.longitude &&
                boundingBoxPixelsY >= minimumPixelDensity.latitude
            ) {
                break;
            }
        }

        if (!lastValidTiles) {
            throw new Error(`Failed to find tiles for bounding box: ${JSON.stringify(request.boundingBox)}`);
        }

        const baseMapInfo = new BaseMapInfo({
            tileMatrixCount: tileMatrixCount,
            tileMatrixIndex: lastValidTileMatrixIndex, //lastValidTileMatrix['ows:Identifier'],
            tileMatrixTileCountX: lastValidTileMatrix.MatrixWidth,
            tileMatrixTileCountY: lastValidTileMatrix.MatrixHeight,

            visibleTiles: lastValidTiles,
        });

        span.end();

        return baseMapInfo;
    }

    public async getTilesAsync(
        tileMatrixIndex: number,
        tiles: { tileX: number; tileY: number }[],
        cachedTiles: MapTile[],
    ) {
        const span = this.tracer.startSpan('fetchTiles');

        await this.ensureTileSetAsync();

        const tileMatrix = this.wmtsUtils.getArray(this.wmtsTileSet.tileMatrixSet.TileMatrix)[tileMatrixIndex];
        const supportedCRS = this.wmtsTileSet.tileMatrixSet['ows:SupportedCRS'].toLowerCase();

        const [topLeftCornerXStr, topLeftCornerYStr] = tileMatrix.TopLeftCorner.split(' ');
        const mapTopLeftCornerX = parseFloat(topLeftCornerXStr);
        const mapTopLeftCornerY = parseFloat(topLeftCornerYStr);
        this.logger.debug('Map left corner:', mapTopLeftCornerX, mapTopLeftCornerY);
        // NOTE(bjarne): This assumes the full map spans the entire world.
        const distancePerTileX = (2 * Math.abs(mapTopLeftCornerX)) / tileMatrix.MatrixWidth;
        const distancePerTileY = (2 * Math.abs(mapTopLeftCornerY)) / tileMatrix.MatrixHeight;
        this.logger.debug('Distances per tile:', distancePerTileX, distancePerTileY);

        const tileData = await Promise.all(
            tiles.map(async (x): Promise<TileData> => {
                const topLeftCornerCoordSpaceX = mapTopLeftCornerX + distancePerTileX * x.tileX;
                const topLeftCornerCoordSpaceY = mapTopLeftCornerY - distancePerTileY * x.tileY;
                const converter = proj4('EPSG:4326', supportedCRS);
                const [longitude, latitude] = converter.inverse([topLeftCornerCoordSpaceX, topLeftCornerCoordSpaceY]);

                if (
                    cachedTiles.find(
                        cached =>
                            cached.tileMatrixIndex === tileMatrixIndex &&
                            cached.tileX === x.tileX &&
                            cached.tileY === x.tileY,
                    )
                ) {
                    // NOTE(bjarne): Unity should already have the image data required, so we just send the rest of
                    // this tile's info. This way Unity knows it is supposed to show this tile.
                    return new TileData({
                        tileMatrixIndex: tileMatrixIndex,
                        tileX: x.tileX,
                        tileY: x.tileY,
                        topLeftCornerU: x.tileX / tileMatrix.MatrixWidth,
                        topLeftCornerV: x.tileY / tileMatrix.MatrixHeight,
                        base64Image: undefined,

                        topLeftCornerLongitude: longitude,
                        topLeftCornerLatitude: latitude,
                    });
                }

                const imageBlob = await this.getTileBlobAsync(
                    this.wmtsTileSet,
                    tileMatrix['ows:Identifier'],
                    x.tileX,
                    x.tileY,
                );

                // const imageObjectURL = URL.createObjectURL(imageBlob);
                // return imageObjectURL;

                // TODO(bjarne): Look into sending the bytes to Unity instead of Base64:
                // https://discussions.unity.com/t/send-byte-array-from-js-to-unity/874743
                const base64 = await this.blobToBase64Async(imageBlob);
                return new TileData({
                    tileMatrixIndex: tileMatrixIndex,
                    tileX: x.tileX,
                    tileY: x.tileY,
                    topLeftCornerU: x.tileX / tileMatrix.MatrixWidth,
                    topLeftCornerV: x.tileY / tileMatrix.MatrixHeight,
                    base64Image: base64,

                    topLeftCornerLongitude: longitude,
                    topLeftCornerLatitude: latitude,
                });
            }),
        );

        span.end();

        return tileData;
    }
}
